import { WidgetHeader } from 'legacy/lut/components/widgets/public/partner360/widgetHeader/WidgetHeader'
import { selectJBPSettings } from 'legacy/lut/features/partner360/jbpSettings'
import { Table } from 'legacy/lut/library/list'
import { FC, useCallback, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { useMeasureController } from 'core/analytics/useMeasureController'
import { ActualsValueTypes } from 'partner360/models/actualValueTypes'
import { getColumns } from 'partner360/widgets/gp360Administration/components/globalPartnerTable/helpers/getColumns'

import { RowButtons } from '../../../../components/rowButtons/RowButtons'
import { getGlobalPartnerDefaultActualsType } from '../../helpers/getGlobalPartnerActualsTypes'
import { getJbpGlobalPartnerAdministrationCrud } from '../../helpers/getJbpGlobalPartnerAdministrationCrud'
import { IGlobalPartner } from '../../models/globalPartner'
import { TRANSLATION_KEYS } from '../../translations'
import { AddNewGlobalPartner } from '../addNewGlobalPartner/AddNewGlobalPartner'
import { GlobalPartnerSearch } from '../globalPartnerSearch/GlobalPartnerSearch'
import { useSearchValue } from '../globalPartnerSearch/hooks/useSearchValue'

import { GlobalPartnerDetailsModal } from './components/globalPartnerDetailsModal/GlobalPartnerDetailsModal'
import { GlobalPartnerTableCell } from './components/globalPartnerTableCell/GlobalPartnerTableCell'
import { GlobalPartnerTableRow } from './components/globalPartnerTableRow/GlobalPartnerTableRow'
import { EGlobalPartnerFilterOptions } from './configs'
import { getFilterByData } from './helpers/getFilterByData'
import { mapGlobalPartnerToForm } from './helpers/mapGlobalPartnerToForm'
import { IGlobalPartnerTableProps } from './models/globalPartnerTableProps'

import styles from './GlobalPartnerTable.module.scss'

export const GlobalPartnerTable: FC<IGlobalPartnerTableProps> = ({
  metrics,
  globalPartnerType,
}) => {
  /** -------------------- State ----------------------- */

  const [globalPartnerFilter, setGlobalPartnerFilter] = useState(EGlobalPartnerFilterOptions.Synced)
  const [partnerDetailsData, setPartnerDetailsData] = useState<IGlobalPartner>()
  const [showGlobalPartnerDetailsModal, setShowGlobalPartnerDetailsModal] = useState(false)
  const [selection, setSelection] = useState<{ rtm: string; value: ActualsValueTypes } | null>(null)
  const valueType =
    selection?.rtm === globalPartnerType
      ? selection.value
      : getGlobalPartnerDefaultActualsType(globalPartnerType)
  const setValueType = (value: ActualsValueTypes) => setSelection({ rtm: globalPartnerType, value })

  /** -------------------- Selectors ----------------------- */

  const { currentFYear } = useSelector(selectJBPSettings)

  /** -------------------- Variables ----------------------- */

  const tableCrud = getJbpGlobalPartnerAdministrationCrud()

  const initialGlobalPartnerFormData = useMemo(() => {
    return mapGlobalPartnerToForm({ globalPartner: partnerDetailsData, globalPartnerType })
  }, [partnerDetailsData, globalPartnerType])

  const crudWithFilters = useMemo(() => {
    tableCrud.setFilter(getFilterByData({ currentFYear, globalPartnerType, globalPartnerFilter }))

    return tableCrud
  }, [currentFYear, globalPartnerFilter, globalPartnerType, tableCrud])

  const columns = useMemo(() => {
    return getColumns(valueType)
  }, [valueType])

  const globalPartnerFilterOptions = useMemo(() => {
    return Object.values(EGlobalPartnerFilterOptions)
  }, [])

  /** -------------------- Hooks ----------------------- */

  const { startLoadData, stopLoadData } = useMeasureController(metrics)
  const { searchValue, updateSearchValue } = useSearchValue()

  /** -------------------- Methods ----------------------- */

  const handleOpenPartnerDetails = useCallback((globalPartner?: IGlobalPartner) => {
    setPartnerDetailsData(globalPartner)
    setShowGlobalPartnerDetailsModal(true)
  }, [])

  const handleClosePartnerDetails = useCallback(() => {
    setPartnerDetailsData(undefined)
    setShowGlobalPartnerDetailsModal(false)
  }, [])

  const setSearchValueToCrudFilters = useCallback(
    (searchValue: string) => {
      tableCrud.setFilter(
        getFilterByData({
          currentFYear,
          globalPartnerType,
          searchValue,
          globalPartnerFilter,
        })
      )
    },
    [globalPartnerFilter, tableCrud, currentFYear, globalPartnerType]
  )

  const generateFilterOptionTranslationKey = useCallback((option: EGlobalPartnerFilterOptions) => {
    return `${TRANSLATION_KEYS.globalPartnerTableFillter}--${option.toLowerCase()}-option`
  }, [])

  /** -------------------- Render ----------------------- */

  return (
    <div>
      <AddNewGlobalPartner
        className={styles.globalPartnerButton}
        onClick={handleOpenPartnerDetails}
      />
      <div className={styles.globalPartnerFilterBlock}>
        <GlobalPartnerSearch
          searchValue={searchValue}
          updateSearchValue={updateSearchValue}
          setSearchValueToCrudFilters={setSearchValueToCrudFilters}
        />
        <div className={styles.globalPartnerHqPartnersCheckbox}>
          <RowButtons
            value={globalPartnerFilter}
            options={globalPartnerFilterOptions}
            generateTranslationKey={generateFilterOptionTranslationKey}
            onOptionClick={setGlobalPartnerFilter}
          />
        </div>
      </div>
      <WidgetHeader>{TRANSLATION_KEYS.globalPartnersTableHeader}</WidgetHeader>
      <Table
        header
        hasTranslate
        columns={columns}
        source={crudWithFilters}
        measure={{ startLoadData, stopLoadData }}
        rowTpl={(item, renderChildren, rowState, rowAttrs, additional) => {
          return (
            <GlobalPartnerTableRow
              rowAttrs={rowAttrs}
              item={item}
              renderChildren={renderChildren}
              rowState={rowState}
              additional={additional}
              metrics={metrics}
              columnLength={columns.length}
              onOpenPartnerDetails={handleOpenPartnerDetails}
              valueType={valueType}
              onChangeValueType={setValueType}
              fYear={currentFYear}
            />
          )
        }}
        cellTpl={(item, column, cellAttrs) => {
          return (
            <GlobalPartnerTableCell
              valueType={valueType}
              searchValue={searchValue}
              key={cellAttrs.key}
              item={item}
              column={column}
              cellAttrs={cellAttrs}
            />
          )
        }}
        pagination
      />
      {showGlobalPartnerDetailsModal && (
        <GlobalPartnerDetailsModal
          initialGlobalPartnerFormData={initialGlobalPartnerFormData}
          globalPartnerCurrentFYearTargetsNdp={partnerDetailsData?.GlobalPartnerTargetsNdp ?? 0}
          globalPartnerCurrentFYearTargetsNet={partnerDetailsData?.GlobalPartnerTargetsNet ?? 0}
          globalPartnerCurrentFYearTargetsTNet={partnerDetailsData?.GlobalPartnerTargetsTNet}
          globalPartnerCurrentFYearTargetsUTNet={partnerDetailsData?.GlobalPartnerTargetsUTNet}
          hqPartnerId={partnerDetailsData?.HQPartnerId ?? null}
          globalPartnerType={globalPartnerType}
          fYear={currentFYear}
          onClose={handleClosePartnerDetails}
          valueType={valueType}
          onChangeValueType={setValueType}
        />
      )}
    </div>
  )
}
